import React, { FC, useState } from "react"
import { useHistory, useParams } from "react-router-dom"
import { reportError, reportErrorMessage } from "utils/ErrorReporting"
import CarrotClient from "lib/carrot-api"
import { FormProvider, useForm } from "react-hook-form"
import { SendMessageForm } from "../../views/talktocarrot/sendmessage/SendMessageForm"
import { useDispatch, useSelector } from "react-redux"
import { getUserInfo } from "../../../reducers/userInfo"
import { signOut } from "../../../actions/loginActions"
import L10n from "../../../lib/carrot-l10n"
import Settings from "../../../utils/CarrotConfig"
import { HttpErrors } from "../../../utils/HttpErrors"
import { getBenefitConfiguration } from "../../../reducers/benefitConfiguration"
import { BenefitConfiguration } from "../../../types/benefitConfiguration"
import { buildMultiElementListString } from "../../../utils/copy/buildMultiElementListString"
import { StringParam, useQueryParam } from "use-query-params"
import { getHeap } from "../../../utils/heap"

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)
const l = new L10n({ locale: "en-US" }).getBoundLocalize()

export type SendMessageContainerContextProps = {
  onSubmit?: () => Promise<void>
  subtitle?: string
  description?: string
  sent?: boolean
  employeeEmail?: string
  personalEmail?: string
  partnerEmail?: string
  sentFromPartnerEmail?: boolean
  options?: object[]
}

const validTopics = [
  "bookappointment",
  "helpfindingprovider",
  "otherprovidertype",
  "stolen",
  "lockedcardsupport",
  "ava-bracelet",
  "legacy",
  "insurance-coverage",
  "MemberHSA_INDIVIDUAL",
  "MemberHSA_FAMILY",
  "PartnerPlanSource",
  "PartnerCoverageType",
  "MemberCoverageType",
  "PartnerHasHSA",
  "ConnectInsuranceError",
  "MemberDeductiblePeriodCoverageDate",
  "PartnerDeductiblePeriodCoverageDate",
  "PartnerTaxDependent"
]

const messageTopicsToSubjectMap = {
  bookappointment: "I'd like to book an appointment",
  helpfindingprovider: "I'd like help finding a provider",
  otherprovidertype: "I'd like help finding adoption, gestational carrier services, or male fertility services",
  stolen: "My Carrot Card was stolen",
  lockedcardsupport: "I'm not able to use my Carrot Card",
  "ava-bracelet": "I have a question about the Ava Bracelet",
  legacy: "I have a question about Legacy",
  "insurance-coverage": "I have a question about insurance coverage",
  MemberHSA_INDIVIDUAL: "I'm not sure if I have an HSA",
  MemberHSA_FAMILY: "I'm not sure if I have an HSA",
  PartnerPlanSource: "I'm not sure where my partner gets their health insurance",
  PartnerCoverageType: "I'm not sure who my partner's health insurance covers",
  MemberCoverageType: "I'm not sure who my health insurance covers",
  PartnerHasHSA: "I'm not sure if my partner has an HSA",
  ConnectInsuranceError: "I'm having trouble connecting to insurance",
  MemberDeductiblePeriodCoverageDate: "I'm not sure when my medical plan coverage begins or ends",
  PartnerDeductiblePeriodCoverageDate: "I'm not sure when my partner's medical plan coverage begins or ends",
  PartnerTaxDependent: "I'm not sure if I claim my partner as a dependent on my tax return"
}

const SendMessageFormContext = React.createContext<SendMessageContainerContextProps>({})

interface SendMessageFormContainerProps {
  BenefitEnrollmentView?: any
  onExit?: () => void
  modalTopic?: string
}

function getInputOrSelectEmail(email) {
  if (typeof email === "string") {
    return email
  }
  return email?.id
}

const SendMessageFormContainer: FC<SendMessageFormContainerProps> = ({ BenefitEnrollmentView, onExit, modalTopic }) => {
  const { personalEmail, email: employeeEmail, partnerEmail } = useSelector(getUserInfo)
  const history = useHistory()
  const [sent, setSent] = useState(false)
  const [sentFromPartnerEmail, setSentFromPartnerEmail] = useState(false)
  const dispatch = useDispatch()
  const email = personalEmail || employeeEmail
  const options = [
    {
      label: email,
      subLabel: personalEmail ? " (your preferred contact email)" : " (your account email)",
      id: email
    },
    {
      label: partnerEmail,
      subLabel: "(your partner's email)",
      id: partnerEmail
    }
  ]

  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      message: "",
      defaultMessage: "Member sent a message with no content"
    }
  })
  const { handleSubmit, setError } = formMethods

  const { providerName, topic: topicFromParams } = useParams<{
    providerName: string
    topic: string
  }>()
  let topic = topicFromParams

  const [enrollmentFlowError] = useQueryParam("enrollmentFlowError", StringParam)

  const benefitConfiguration = useSelector(getBenefitConfiguration)

  // eslint-disable-next-line
  // @ts-ignore
  const onSubmit = handleSubmit(async ({ email: emailOption, message, defaultMessage }) => {
    try {
      const email = getInputOrSelectEmail(emailOption)
      const sendFromPartner = email === partnerEmail
      const sendMessage = message ? message : defaultMessage
      setSentFromPartnerEmail(sendFromPartner)
      await client.sendTalkToCarrotMessage(sendMessage, messageTopicsToSubjectMap[topic], sendFromPartner, providerName)
      if (enrollmentFlowError) {
        getHeap().track("EnrollmentFlowSupportRequest", { EventName: enrollmentFlowError })
      }
    } catch (error) {
      if (error.name === HttpErrors.UNAUTHORIZED) {
        dispatch(signOut())
        return
      }
      reportError(error)

      setError("message", {
        type: "internalError",
        message: "We ran into an issue while sending your message. Please try again."
      })
      return
    }
    setSent(true)
  })

  // Inserting BOOK_APPOINTMENT topic is required since providerName comes from a different route that doesn't have `topic`.
  // Prevents "undefined" topic
  topic = !!providerName ? "bookappointment" : topic
  topic = modalTopic ? modalTopic : topic

  if (!validTopics.includes(topic)) {
    history.push("/talk-to-carrot")
    reportErrorMessage(`Invalid topic passed into SendMessageForm: ${topic}`)
    return null
  }

  // Get page specific heading/description. If it doesn't exist, grab the category page on
  const heading = getPageSpecificHeadingOrFallback(topic, benefitConfiguration)
  const description = getPageSpecificDescriptionOrFallback(topic)

  const context = {
    onSubmit,
    subtitle: heading,
    description: description,
    sent,
    personalEmail: personalEmail,
    employeeEmail: email,
    partnerEmail: partnerEmail,
    sentFromPartnerEmail: sentFromPartnerEmail,
    options
  }

  return (
    <SendMessageFormContext.Provider value={context}>
      <FormProvider {...formMethods}>
        {BenefitEnrollmentView ? <BenefitEnrollmentView onExit={onExit} /> : <SendMessageForm />}
      </FormProvider>
    </SendMessageFormContext.Provider>
  )
}

const benefitTopicCopy = [
  {
    text: "adoption",
    filter: (c) => c.featureConfig.isAdoptionEnabled
  },
  {
    text: "gestational carrier services",
    filter: (c) => c.featureConfig.isGcEnabled
  },
  {
    text: "male fertility services"
  }
]

const getPageSpecificHeadingOrFallback = (topic: string, benefitConfiguration: BenefitConfiguration) => {
  const headingKey = `ttc.${topic}.sendMessagePage.heading`
  const filteredBenefitTopic = benefitTopicCopy
    .filter((c) => !c.filter || c.filter(benefitConfiguration))
    .map((item) => item.text)

  const effectiveBenefitTopic = buildMultiElementListString(filteredBenefitTopic, "or")

  return l(headingKey) !== headingKey
    ? l(headingKey, { benefitTopic: effectiveBenefitTopic })
    : l(`ttc.${topic}.categoryPage.heading`)
}

const getPageSpecificDescriptionOrFallback = (topic: string) => {
  const descriptionKey = `ttc.${topic}.sendMessagePage.description`
  return l(descriptionKey) !== descriptionKey ? l(descriptionKey) : l(`ttc.${topic}.categoryPage.description`)
}

export { SendMessageFormContainer, SendMessageFormContext }
